/* Dependencies */
import Siema from 'siema';

// Models
import { ProductCarouselElements } from './ProductCarousel.model';

/**
 * ProductCarousel
 * @class
 */
class ProductCarousel {
  public elements: ProductCarouselElements;
  public carousel: Siema;

  constructor(elements: ProductCarouselElements) {
    this.elements = elements;

    // Create a carousel instance.
    this.carousel = new Siema({
      selector: this.elements.carouselMainElement,
      duration: 200,
      easing: 'linear',
      perPage: 1,
      loop: false,
      onChange: () => {
        this.updateActiveItem();
      },
    });

    setTimeout(() => {
      this.updateActiveItem();
      this.updateThumbnailHeight();
    });

    // Bind the required event.
    this.bindEvents();
  }

  /**
   * Bind the required events.
   */
  bindEvents(): void {
    this.elements.thumbnailElements.forEach((thumbnailElement, index) => {
      thumbnailElement.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    let resizeTimeout: NodeJS.Timeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateThumbnailHeight();
      }, 50);
    });
  }

  /**
   * Update the thumbnail height.
   */
  updateThumbnailHeight(): void {
    this.elements.thumbnailContainerElement.style.maxHeight = `${this.elements.carouselMainElement.clientHeight}px`;
  }

  /**
   * Go to a desired slide.
   * @param index - The index of the slide to go to.
   */
  goToSlide(index: number): void {
    this.carousel.goTo(index);
    this.updateActiveItem();
    this.updateLiveRegion();
  }

  /**
   * Navigate to a slide based on the image id.
   * Used for product variant changes.
   */
  goToSlideId(id: number): void {
    // Locate the slide index
    const index = this.elements.thumbnailElements.findIndex(
      (thumbnailElement) => {
        const thumbnailId = thumbnailElement.getAttribute('data-image-id');
        return thumbnailId ? +thumbnailId === id : false;
      }
    );

    // Navigate if found
    if (index !== -1) {
      this.goToSlide(index);
    }
  }

  /**
   * Update the live region.
   */
  updateLiveRegion(): void {
    this.elements.liveRegionElement.textContent = `Now Showing Slide ${
      this.carousel.currentSlide + 1
    }`;
  }

  /**
   * Update to reflect the active slide.
   */
  updateActiveItem(): void {
    // Remove the current active class.
    this.elements.thumbnailElements.forEach((thumbnailElement, index) => {
      thumbnailElement.classList.remove('tw-border-black');
      thumbnailElement.setAttribute('aria-current', 'false');
      thumbnailElement.setAttribute('aria-label', `Go to slide ${index + 1}`);
    });

    // Set the new active item.
    this.elements.thumbnailElements[this.carousel.currentSlide].classList.add(
      'tw-border-black'
    );
    this.elements.thumbnailElements[this.carousel.currentSlide].setAttribute(
      'aria-current',
      'true'
    );
    this.elements.thumbnailElements[this.carousel.currentSlide].setAttribute(
      'aria-label',
      'Current Slide'
    );
  }
}

// Expost a shared instance.
export let productCarousel: ProductCarousel;

/**
 * Initiates the product carousel.
 * @returns
 */
export const productCarouselInit = (): void => {
  // Retireve the carousel container
  const productCarouselContainer = document.querySelector(
    '[data-product-carousel]'
  );

  // If no slider elements, return.
  if (!productCarouselContainer) return;

  // Retireve the carousel elements.
  const thumbnailContainerElement: HTMLElement =
    productCarouselContainer.querySelector(
      '[data-product-carousel-thumbnails]'
    );
  const carouselMainElement: HTMLElement =
    productCarouselContainer.querySelector('[data-product-carousel-main]');

  const thumbnailElements: HTMLElement[] = Array.from(
    thumbnailContainerElement.querySelectorAll(
      '[data-product-carousel-thumbnails-trigger]'
    )
  );

  const liveRegionElement: HTMLElement = productCarouselContainer.querySelector(
    '[data-product-carousel-live-region]'
  );

  // Create the carousel
  productCarousel = new ProductCarousel({
    thumbnailContainerElement,
    carouselMainElement,
    thumbnailElements,
    liveRegionElement,
  });
};
